import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { doc, getDoc } from "firebase/firestore";
import { format } from "date-fns";
import { db } from "../../firebase";
import ReactToPrint from "react-to-print";
import "./BagSearchChecklistRedesign.css";
import { StandardDrawerFooter, StandardDrawerHeader } from "./StandardDrawer";
import FormContentSkeleton from "./FormContentSkeleton";

function ViewClientIntakeAndBagSearchChecklist(props) {
  const { clientId, closemodal, firstName, lastName } = props;
  const [data, setData] = useState(null);
  const [signaturePic, setSignaturePic] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const printRef = useRef();

  const fetchData = useCallback(async () => {
    try {
      const docRef = doc(db, "ClientIntakeAndBagSearchChecklist", clientId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const fetchedData = docSnap.data();
        setData(fetchedData);
        if (fetchedData.signatureURL) {
          setSignaturePic(fetchedData.signatureURL);
        }
      } else {
        console.log("No checklist found for this client.");
      }
    } catch (error) {
      console.error("Error fetching checklist:", error);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData().finally(() => setIsDataLoading(false));
  }, [clientId, fetchData]);

  function formatDate(timestamp) {
    if (!timestamp || !timestamp.toDate) return "";
    return format(timestamp.toDate(), "dd MMM yyyy, HH:mm");
  }

  // Sections définies comme dans AddClientIntakeAndBagSearchChecklist
  const checklistSections = [
    {
      title: "Initial Greeting and Explanation",
      items: [
        "Explain the importance of the search process for safety and compliance.",
        "Inform the client of the steps involved and ask if they have any questions.",
      ],
    },
    {
      title: "Body and Personal Item Check",
      items: [
        "Emptying Pockets: Ask the client to empty all pockets and place items on a tray.",
        "Conduct a pat-down from head to toe, checking: Quick Pat-Down, Waistband, Underarms, Inside collars and cuffs, Socks and shoes.",
      ],
    },
    {
      title: "Bag and Luggage Search",
      items: [
        "External Inspection: Check for signs of tampering (e.g., cut fabric, sewn-in pockets, broken zippers). Look for unusual bulges or weight distribution.",
        "Internal Inspection: Open all zippers, pockets, and compartments. Shake out all clothing and inspect seams and linings for hidden pockets. Check the bottoms of bags and behind any interior linings. Inspect containers, toiletry bags, and electronics cases.",
      ],
    },
    {
      title: "Main Luggage Search",
      items: [
        "External Inspection: Look for any signs of tampering or unusual modifications. Check handles, wheels, and any external pockets.",
        "Internal Inspection: Open all compartments and remove items. Shake out clothing and check for hidden pockets. Inspect seams, linings, and any rigid structures for hidden items. Thoroughly check shoes, books, and any other personal items.",
      ],
    },
    {
      title: "Electronic Devices Check",
      items: [
        "Ask the client to declare all electronic devices (e.g., laptops, tablets, phones). Document each device and the client's stated need for them.",
      ],
    },
    {
      title: "Documentation",
      items: [
        "Quick Note: Immediately after the search, document the client’s name and date of intake, details of the search process (what was checked and any items found), and information explained to the client about the search and next steps. Note that the client has been shown around or that orientation will occur later.",
        "End of Shift Note: Briefly mention that the bag search was completed. Refer to the quick note for detailed information.",
      ],
    },
  ];

  return (
    <>
      <style>
        {`
          @media print {
            #printableContent {
              height: auto !important;
              overflow-y: visible !important;
            }
          }
        `}
      </style>
      <div className="bag-checklist-shell">
        <StandardDrawerHeader
          eyebrow="Admission form"
          title="Client intake and bag search checklist"
          subtitle={`${firstName} ${lastName}${data && data.signedOn ? ` · Signed on ${formatDate(data.signedOn)}` : ""}`}
          onClose={closemodal}
        />

        <div
          id="printableContent"
          className={`bag-checklist-body ${isDataLoading ? "form-content-loading" : ""}`}
          ref={printRef}
        >
          <FormContentSkeleton rows={5} />
          <div className="bag-checklist-intro">
            <strong>Completed checklist</strong>
            <span>Read-only record of the completed intake and bag search.</span>
          </div>

          {data && data.responses ? (
            checklistSections.map((section, sIndex) => (
              <section className="bag-checklist-card" key={sIndex}>
                <h4>{section.title}</h4>
                <div className="bag-checklist-items">
                {section.items.map((item, iIndex) => {
                  const key = `${sIndex}_${iIndex}`;
                  return (
                    <div
                      key={key}
                      className="bag-checklist-item bag-checklist-item-readonly"
                    >
                      <Checkbox
                        checked={data.responses[key] || false}
                        disabled
                        className="bag-checklist-checkbox"
                      />
                      <span>{item}</span>
                    </div>
                  );
                })}
                </div>
              </section>
            ))
          ) : (
            <div className="bag-checklist-empty">No checklist data found.</div>
          )}

          {signaturePic && (
            <section className="bag-checklist-card bag-checklist-signature-card">
              <h4>Client signature</h4>
              <img
                alt="Client Signature"
                src={signaturePic}
                className="bag-checklist-signature-image"
              />
            </section>
          )}
        </div>

        <StandardDrawerFooter className="bag-checklist-footer">
          <Button
            className="bag-checklist-secondary-button"
            variant="contained"
            onClick={closemodal}
          >
            Cancel
          </Button>
          <ReactToPrint
            trigger={() => (
              <Button
                className="bag-checklist-primary-button"
                variant="contained"
                startIcon={<PrintIcon />}
              >
                Print
              </Button>
            )}
            content={() => printRef.current}
            onAfterPrint={closemodal}
          />
        </StandardDrawerFooter>
      </div>
    </>
  );
}

export default ViewClientIntakeAndBagSearchChecklist;
